import React, { useEffect, createElement } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Phone, Info } from 'lucide-react';
import { SocialProofBar } from '../SocialProofBar';
import { LeadForm } from '../LeadForm';
import { TrustBadges } from '../TrustBadges';
import { BenefitCard } from '../BenefitCard';
import { ReviewCard } from '../ReviewCard';
import { ScheduleTable } from '../ScheduleTable';
import { LocationBlock } from '../LocationBlock';
import { FAQAccordion } from '../FAQAccordion';
import { CTABand } from '../CTABand';
interface AdLandingTemplateProps {
  h1: string;
  subheadline: string;
  ctaText: string;
  searchIntent: string;
  heroImage: string;
  benefits: {
    icon: React.ElementType;
    title: string;
    description: string;
  }[];
  whatYouLearn: string[];
  faqs: {
    question: string;
    answer: string;
  }[];
  programLinks?: {
    title: string;
    href: string;
  }[];
  karateBridge?: boolean;
  showForm?: boolean;
  videoSrc?: string;
  videoPoster?: string;
  videoHeadline?: string;
  videoCopy?: string;
  metaTitle: string;
  metaDescription: string;
}
export function AdLandingTemplate({
  h1,
  subheadline,
  ctaText,
  searchIntent,
  heroImage,
  benefits,
  whatYouLearn,
  faqs,
  programLinks,
  karateBridge,
  showForm = true,
  videoSrc,
  videoPoster,
  videoHeadline,
  videoCopy,
  metaTitle,
  metaDescription
}: AdLandingTemplateProps) {
  useEffect(() => {
    document.title = metaTitle;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', metaDescription);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = metaDescription;
      document.head.appendChild(meta);
    }
  }, [metaTitle, metaDescription]);
  return <div className="flex flex-col bg-off-white" data-id="element-270">
      {/* Hero Section */}
      <section className="relative bg-ink pt-12 pb-24 lg:pt-20 lg:pb-32 overflow-hidden" data-id="element-271">
        <div className="absolute inset-0 opacity-20" data-id="element-272">
          <img src={heroImage} alt="Taekwondo Background" className="w-full h-full object-cover" data-id="element-273" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/90 to-transparent" data-id="element-274" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-275">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-276">
            <div className="flex flex-col items-start" data-id="element-277">
              <div className="inline-block bg-storm-red text-white px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wide mb-6" data-id="element-278">
                {searchIntent}
              </div>
              <div className="mb-8" data-id="element-279">
                <SocialProofBar data-id="element-280" />
              </div>
              <h1 className="text-5xl md:text-6xl text-white mb-6 leading-[1.1]" data-id="element-281">
                {h1}
              </h1>
              <p className="text-xl text-off-white mb-8 max-w-xl leading-relaxed" data-id="element-282">
                {subheadline}
              </p>

              <ul className="flex flex-col gap-3 mb-10" data-id="element-283">
                {whatYouLearn.slice(0, 4).map((item, idx) => <li key={idx} className="flex items-center gap-3 text-white text-lg" data-id="element-284">
                    <CheckCircle2 className="w-6 h-6 text-storm-red shrink-0" data-id="element-285" />
                    <span data-id="element-286">{item}</span>
                  </li>)}
              </ul>

              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto mb-10" data-id="element-287">
                <Link to="/free-trial" className="btn-primary" data-id="element-288">
                  {ctaText}
                </Link>
                <a href="tel:517-220-0621" className="btn-secondary" data-id="element-289">
                  Call 517-220-0621
                </a>
              </div>
              <TrustBadges data-id="element-290" />
            </div>

            <div className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto" data-id="element-291">
              {showForm ? <div className="bg-paper p-8 rounded-2xl shadow-2xl border border-subtle-gray relative" data-id="element-292">
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-storm-red text-white px-6 py-2 rounded-full font-bold uppercase tracking-wide whitespace-nowrap shadow-lg" data-id="element-293">
                    Claim Your Free Week
                  </div>
                  <div className="mt-4" data-id="element-294">
                    <LeadForm buttonText={ctaText} data-id="element-295" />
                  </div>
                </div> : <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-subtle-gray aspect-[4/5]" data-id="element-296">
                  <img src={heroImage} alt="Storm Taekwondo Training" className="w-full h-full object-cover" data-id="element-297" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/95 via-ink/40 to-transparent" data-id="element-298" />
                  <div className="absolute bottom-0 left-0 right-0 p-8 flex flex-col gap-4" data-id="element-299">
                    <div className="inline-flex items-center gap-2 bg-storm-red text-white px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide w-fit" data-id="element-300">
                      Free One-Week Trial
                    </div>
                    <h3 className="text-3xl text-white m-0 leading-tight" data-id="element-301">
                      Ready to get started?
                    </h3>
                    <Link to="/free-trial" className="btn-primary w-full text-center" data-id="element-302">
                      {ctaText}
                    </Link>
                    <a href="tel:517-220-0621" className="text-white font-bold text-center hover:text-storm-red transition-colors flex items-center justify-center gap-2" data-id="element-303">
                      <Phone className="w-4 h-4" data-id="element-304" />
                      Or call 517-220-0621
                    </a>
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </section>

      {/* Karate Bridge Explainer */}
      {karateBridge && <section className="py-12 bg-storm-red text-white" data-id="element-305">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-306">
            <div className="flex items-center justify-center gap-3 mb-4" data-id="element-307">
              <Info className="w-8 h-8" data-id="element-308" />
              <h2 className="text-3xl m-0" data-id="element-309">Looking for Karate?</h2>
            </div>
            <p className="text-xl leading-relaxed" data-id="element-310">
              Many parents search for "karate" when they mean martial arts.
              Storm teaches Olympic-style Taekwondo, a dynamic martial art that
              develops discipline, confidence, and self-defense skills.
            </p>
          </div>
        </section>}

      {/* Video Section */}
      {videoSrc && <section className="py-24 bg-ink relative overflow-hidden" data-id="element-311">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-charcoal transform skew-x-12 translate-x-32 opacity-30 pointer-events-none" data-id="element-312" />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-313">
            <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-314">
              <div className="inline-block bg-storm-red text-white px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wide mb-4" data-id="element-315">
                See It In Action
              </div>
              <h2 className="text-4xl md:text-5xl text-white mb-4" data-id="element-316">
                {videoHeadline || 'Watch Storm Taekwondo in Action'}
              </h2>
              {videoCopy && <p className="text-xl text-gray-warm leading-relaxed" data-id="element-317">
                  {videoCopy}
                </p>}
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border-2 border-charcoal aspect-video bg-charcoal" data-id="element-318">
              <video src={videoSrc} poster={videoPoster} controls playsInline preload="metadata" className="w-full h-full object-cover" data-id="element-319">
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </section>}

      {/* Benefits Section */}
      <section className="py-24 bg-paper border-y border-subtle-gray" data-id="element-320">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-321">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-322">
            <h2 className="text-4xl md:text-5xl text-ink mb-6" data-id="element-323">
              Why Families Choose Storm
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-324">
            {benefits.map((benefit, idx) => <BenefitCard key={idx} icon={benefit.icon} title={benefit.title} description={benefit.description} data-id="element-325" />)}
          </div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="py-24 bg-off-white" data-id="element-326">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-327">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-328">
            <div data-id="element-329">
              <h2 className="text-4xl md:text-5xl text-ink mb-8" data-id="element-330">
                What You'll Learn
              </h2>
              <ul className="flex flex-col gap-4" data-id="element-331">
                {whatYouLearn.map((item, idx) => <li key={idx} className="flex items-start gap-4 text-lg text-gray-warm" data-id="element-332">
                    <CheckCircle2 className="w-6 h-6 text-storm-red shrink-0 mt-0.5" data-id="element-333" />
                    <span data-id="element-334">{item}</span>
                  </li>)}
              </ul>
              <div className="mt-10" data-id="element-335">
                <Link to="/free-trial" className="btn-primary" data-id="element-336">
                  {ctaText}
                </Link>
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-xl h-[400px]" data-id="element-337">
              <img src={heroImage} alt="Training at Storm Taekwondo" className="w-full h-full object-cover" data-id="element-338" />
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-24 bg-paper border-y border-subtle-gray" data-id="element-339">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-340">
          <h2 className="text-4xl md:text-5xl text-ink text-center mb-16" data-id="element-341">
            Real Reviews from Local Families
          </h2>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-342">
            <ReviewCard quote="My son has been going here for 6 months and his confidence has skyrocketed. The coaches are amazing with the kids." name="Sarah M." label="Parent Review" data-id="element-343" />
            <ReviewCard quote="Great family atmosphere. We started in the family class and it's been the best decision we've made for our health." name="David R." label="Parent Review" data-id="element-344" />
            <ReviewCard quote="The discipline and focus my daughter has learned here translates directly to her schoolwork. Highly recommend Storm!" name="Jennifer L." label="Parent Review" data-id="element-345" />
          </div>
        </div>
      </section>

      {/* Schedule & Location */}
      <section className="py-24 bg-off-white" data-id="element-346">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-347">
          <div className="grid lg:grid-cols-2 gap-16" data-id="element-348">
            <div data-id="element-349">
              <h2 className="text-4xl text-ink mb-8" data-id="element-350">Class Times</h2>
              <ScheduleTable data-id="element-351" />
            </div>
            <div data-id="element-352">
              <h2 className="text-4xl text-ink mb-8" data-id="element-353">
                Taekwondo Classes in Farmington Hills, MI
              </h2>
              <LocationBlock data-id="element-354" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-paper border-t border-subtle-gray" data-id="element-355">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-356">
          <h2 className="text-4xl md:text-5xl text-ink text-center mb-16" data-id="element-357">
            Frequently Asked Questions
          </h2>
          <FAQAccordion faqs={faqs} data-id="element-358" />
        </div>
      </section>

      {/* Final CTA */}
      <CTABand headline="Ready to Try Storm Taekwondo for Free?" copy="Give your child, yourself or your family a chance to experience Storm before committing. Claim a free week and see why local families choose Storm Taekwondo." data-id="element-359" />
    </div>;
}